import React from 'react';
import {FeatureFlagEnum, ModuleEnum} from 'src/api/ProactApiClient';
import {ModuleExtensionRegistry} from 'src/shared/constants/moduleExtensionRegistry';
import {useGetActiveExtensionsQuery} from 'src/redux/queries/moduleExtension';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import useActiveFeatureFlags from 'src/shared/hooks/useActiveFeatureFlags';
import {Alert} from 'src/design/atoms/Alert';
import {Card} from 'src/design/atoms/Card';
import {Center} from 'src/design/atoms/Center';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Text} from 'src/design/atoms/Text';
import {UnstyledButton} from 'src/design/atoms/UnstyledButton';
import {IconChevronRight} from '@tabler/icons-react';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

const ModuleFeatureFlagMap: Partial<Record<ModuleEnum, FeatureFlagEnum>> =
  Object.fromEntries(
    Object.entries(ModuleExtensionRegistry).map(([module, config]) => [
      module,
      config.featureFlag,
    ])
  ) as Partial<Record<ModuleEnum, FeatureFlagEnum>>;

const ModuleDisplayNames: Partial<Record<ModuleEnum, string>> =
  Object.fromEntries(
    Object.entries(ModuleExtensionRegistry).map(([module, config]) => [
      module,
      config.displayName,
    ])
  ) as Partial<Record<ModuleEnum, string>>;

export const ModulesTab: React.FC = () => {
  const navigate = useAppNavigate();
  const activeFeatureFlags = useActiveFeatureFlags();
  const {
    data: activeExtensions,
    isLoading,
    isError,
  } = useGetActiveExtensionsQuery();

  if (isLoading) {
    return (
      <Center mt="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (isError || !activeExtensions) {
    return (
      <Alert color="red" title="Failed to load modules">
        The list of customizable modules could not be loaded. Please try again.
      </Alert>
    );
  }

  const supportedModules = activeExtensions.supportedModules ?? [];

  const enabledModules = supportedModules.filter(module => {
    const featureFlag = ModuleFeatureFlagMap[module];
    if (featureFlag === undefined) return true;
    if (featureFlag === FeatureFlagEnum.Invalid) return true;
    return activeFeatureFlags?.includes(featureFlag);
  });

  if (enabledModules.length === 0) {
    return <Text c="dimmed">No modules are available for customization.</Text>;
  }

  return (
    <SimpleGrid cols={{base: 1, sm: 2, md: 3}}>
      {enabledModules.map(module => (
        <UnstyledButton
          key={module}
          onClick={() =>
            navigate(
              PathUtility.ModuleCustomizationDetail(
                ModuleEnum[module].toLowerCase()
              )
            )
          }>
          <Card shadow="sm" padding="lg" withBorder>
            <Group justify="space-between">
              <Text fw={500}>
                {ModuleDisplayNames[module] ?? ModuleEnum[module]}
              </Text>
              <IconChevronRight size={16} />
            </Group>
          </Card>
        </UnstyledButton>
      ))}
    </SimpleGrid>
  );
};
